// topRightMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import Loading from './loading.jsx';
import { fetchSampleInfo, fetchMultipleSamplesMetaInfo, fetchMultipleSamplesTaxaAggregateInfo, fetchMultipleSamplesInfoBig } from './api.jsx';
import HorizontalColoredLine from './widgets/horizontal_line.jsx';
import Button from './widgets/button.jsx';
import { useNavigate } from 'react-router-dom';
import SunburstPlot from './widgets/SunburstPlot.jsx';
import buildTaxonomicTreeSingle from '../utils/makeNestedStructure.jsx';
import buildTaxonomicTreeMultiple from '../utils/makeNestedStructureMultiple.jsx';
import HorizontalTabs from './widgets/HorizontalTabs.jsx';
import SubSampleBiome from './SubSampleBiome.jsx';
import SubSampleBiomeColorOther from './subSampleBiomeColorOther.jsx';
import SubSampleTaxonomy from './SubSampleTaxonomy.jsx';
import SubSampleStudies from './SubSampleStudies.jsx';
import SubSampleEnrichmentScatter from './subSampleEnrichmentScatter.jsx';
import SubSampleUMAP from './subSampleUMAP.jsx';
import SubSampleTaxaDistribution from './subSampleTaxaDistribution.jsx';
import { useTheme } from '../theme/ThemeContext.jsx';
import NeighborSelector from './widgets/neighborSelector';
import NeighborSelectorMultiple from './widgets/neighborSelectorMultiple.jsx';

import * as d3 from 'd3';

const TopRightMenu = ({
    scatterplot,
    selectedSampleInfo,
    possibleColorDict,
    highlightTaxon,
    setHighlightTaxon,
    colors,
    colorDict,
    points,
    ids,
    currentPointsSelection,
    totalBiomeCounts,
}) => {
    const { theme } = useTheme();
    const [singleSampleData, setSingleSampleData] = useState(null);
    const [multipleSamplesMetaData, setMultipleSamplesMetaData] = useState(null);
    const [multipleSamplesTaxaData, setMultipleSamplesTaxaData] = useState(null);
    const [multipleSamplesTaxaDataIndividual, setMultipleSamplesTaxaDataIndividual] = useState(null);
    const [selectedRankIndex, setSelectedRankIndex] = useState(5);
    const [taxonomyTreeData, setTaxonomyTreeData] = useState(null);
    const [taxonomyTreeDataMultiple, setTaxonomyTreeDataMultiple] = useState(null);
    const [selectedTab, setSelectedTab] = useState('Biome');
    const [loadingSingleSampleData, setLoadingSingleSampleData] = useState(false);
    const [loadingMultipleSamplesMetaData, setLoadingMultipleSamplesMetaData] = useState(true);
    const [loadingMultipleSamplesTaxaData, setLoadingMultipleSamplesTaxaData] = useState(true);
    const [loadingMultipleIndividualSamplesTaxaData, setLoadingMultipleIndividualSamplesTaxaData] = useState(true);
    const [domain, setDomain] = useState('bacteria');
    const [availableDomains, setAvailableDomains] = useState([]);
    const [neighborCount, setNeighborCount] = useState(500);
    // const [neighborCountMultiple, setNeighborCountMultiple] = useState(10);
    const [neighborCountMultiple, setNeighborCountMultiple] = useState(500);
    const [neighborSelectionMethod, setNeighborSelectionMethod] = useState('for combined');
    const [sunburstDepth, setSunburstDepth] = useState(3);

    // const [domain, setDomain] = useState('archaea');


    const abortControllerRef = useRef(null);


    const divStyleRight = {
        position: 'fixed',
        top: 0,
        right: 0,
        width: '25vw',
        margin: '40px',
        padding: '20px',
        color: '#dadada',
        fontFamily: 'Arial',
        backgroundColor: theme.colors.overlay,
        borderRadius: '10px',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        transition: 'all 0.3s ease-in-out',
        maxHeight: '80vh',
        // overflow: 'auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        // minWidth: '300px',
        // overflow: 'display',

    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
    };

    const toggleDomain = () => {
        setDomain(prev => (prev === 'bacteria' ? 'archaea' : 'bacteria'));
    };

    useEffect(() => {
        const fetchData = async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            if (!selectedSampleInfo || selectedSampleInfo.length === 0) {
                setSingleSampleData(null);
                setMultipleSamplesMetaData(null);
                setSelectedTab('Biome');
                return;
            }

            if (selectedSampleInfo.length === 1) {
                setLoadingSingleSampleData(true);
                try {
                    const data = await fetchSampleInfo(
                        selectedSampleInfo[0],
                        abortControllerRef.current.signal
                    );
                    setSingleSampleData(data);
                    setLoadingSingleSampleData(false);
                } catch (error) {
                    if (error.name === 'AbortError') return;
                    console.error(error);
                    setLoadingSingleSampleData(false);
                }
            } else {
                const sampleIds = selectedSampleInfo.flat();

                setLoadingMultipleSamplesMetaData(true);
                setLoadingMultipleSamplesTaxaData(true);
                setLoadingMultipleIndividualSamplesTaxaData(true);

                // metadata (fast)
                fetchMultipleSamplesMetaInfo(sampleIds, abortControllerRef.current.signal)
                    .then(metaData => {
                        setMultipleSamplesMetaData(metaData);
                        setLoadingMultipleSamplesMetaData(false);
                    })
                    .catch(error => {
                        if (error.name === 'AbortError') return;
                        console.error('Metadata fetch error:', error);
                        setLoadingMultipleSamplesMetaData(false);
                    });

                // aggregate taxa data (medium)
                fetchMultipleSamplesTaxaAggregateInfo(sampleIds, abortControllerRef.current.signal)
                    .then(taxaData => {
                        setMultipleSamplesTaxaData(taxaData);
                        setLoadingMultipleSamplesTaxaData(false);
                    })
                    .catch(error => {
                        if (error.name === 'AbortError') return;
                        console.error('Taxa aggregate fetch error:', error);
                        setLoadingMultipleSamplesTaxaData(false);
                    });

                // individual taxa data (slow)
                if (sampleIds.length <= 5000) {
                    // might be smarter to only fetch taxa form a given rank, this would improve the speed by a lot.
                    fetchMultipleSamplesInfoBig(sampleIds, abortControllerRef.current.signal)
                        .then(taxaDataIndividual => {
                            setMultipleSamplesTaxaDataIndividual(taxaDataIndividual);
                            setLoadingMultipleIndividualSamplesTaxaData(false);
                        })
                        .catch(error => {
                            if (error.name === 'AbortError') return;
                            console.error('Individual taxa fetch error:', error);
                            setLoadingMultipleIndividualSamplesTaxaData(false);
                        });
                } else {
                    setLoadingMultipleIndividualSamplesTaxaData(false);
                }
            }
        };

        fetchData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [selectedSampleInfo]);

    useEffect(() => {
        if (singleSampleData?.taxonomic_data) {
            try {
                const { tree, availableDomains } = buildTaxonomicTreeSingle(singleSampleData.taxonomic_data, domain);
                setAvailableDomains(availableDomains);

                if (!availableDomains.includes(domain)) {
                    setDomain('bacteria');
                    return;
                }

                setTaxonomyTreeData(tree);
            } catch (error) {
                console.error('Error building taxonomy tree:', error);
                setTaxonomyTreeData(null);
            }
        }
    }, [singleSampleData, domain]);

    useEffect(() => {
        if (multipleSamplesTaxaData) {
            try {
                // const treeStructure = buildTaxonomicTreeMultiple(multipleSamplesTaxaData);
                const { tree: treeStructure, availableDomains: multipleAvailableDomains } = buildTaxonomicTreeMultiple(multipleSamplesTaxaData, domain);
                setAvailableDomains(multipleAvailableDomains);

                if (!multipleAvailableDomains.includes(domain)) {
                    setDomain('bacteria');
                    return;
                }

                if (!treeStructure) {
                    console.warn('buildTaxonomicTreeMultiple returned null/undefined');
                    setTaxonomyTreeDataMultiple(null);
                    setLoadingMultipleSamplesTaxaData(false);
                    return;
                }

                // create hierarchy with null check
                const hierarchyData = d3.hierarchy(treeStructure);
                if (!hierarchyData) {
                    console.warn('d3.hierarchy returned null/undefined');
                    setTaxonomyTreeDataMultiple(null);
                    setLoadingMultipleSamplesTaxaData(false);
                    return;
                }

                setTaxonomyTreeDataMultiple(hierarchyData.data);
                setLoadingMultipleSamplesTaxaData(false);
            } catch (error) {
                console.error('Error creating taxonomy tree:', error);
                setTaxonomyTreeDataMultiple(null);
                setLoadingMultipleSamplesTaxaData(false);
            }
        }
    }, [multipleSamplesTaxaData, domain]);

    useEffect(() => {
        if (multipleSamplesTaxaDataIndividual) {
            // console.log(multipleSamplesTaxaDataIndividual);
        }
    }, [multipleSamplesTaxaDataIndividual]);



    const handleRankChange = (event) => {
        setSelectedRankIndex(Number(event.target.value));
    };

    const selectNeighbors = (n = 10) => {
        if (!selectedSampleInfo || selectedSampleInfo.length !== 1 || !points || points.length === 0) {
            console.log("only one point can be selected");
            return;
        }

        // index of selected point
        const selectedId = selectedSampleInfo[0];
        const selectedIndex = ids.indexOf(selectedId);
        const selectedPoint = points[selectedIndex];

        // does not work on chrome for some reason (works on firefox)
        const distances = points.map((point, index) => {
            if (index === selectedIndex) return Infinity;

            // euclidean distance
            const dx = point[0] - selectedPoint[0];
            const dy = point[1] - selectedPoint[1];
            const distance = Math.sqrt(dx * dx + dy * dy);

            // NOTE t-SNE distances are not linearly related to euclidean distances

            return {
                index: index,
                distance: distance
            };
        });

        const nearestNeighbors = distances
            .sort((a, b) => a.distance - b.distance)
            .slice(0, n)
            .map(d => d.index);

        nearestNeighbors.push(selectedIndex);
        // console.log(nearestNeighbors);


        if (scatterplot) {
            scatterplot.select(nearestNeighbors);
        }
    };

    const selectNeighborsMultipleForEach = (n = 10) => {
        if (!selectedSampleInfo || selectedSampleInfo.length === 1 || !points || points.length === 0) {
            console.log("multiple samples must be selected");
            return;
        }
        // console.log(selectedSampleInfo);


        // get indices of all selected points
        const selectedIndices = selectedSampleInfo.map(id => ids.indexOf(id));

        // set to store unique neighbor indices
        const neighborSet = new Set();

        // for each selected point
        selectedIndices.forEach(selectedIndex => {
            if (selectedIndex === -1) return; // skip if sample ID not found

            const selectedPoint = points[selectedIndex];

            // calculate distances from this point to all other points
            const distances = points.map((point, index) => {
                if (selectedIndices.includes(index)) return { index, distance: Infinity }; // Exclude selected points

                // calculate euclidean distance
                const dx = point[0] - selectedPoint[0];
                const dy = point[1] - selectedPoint[1];
                const distance = Math.sqrt(dx * dx + dy * dy);

                return {
                    index: index,
                    distance: distance
                };
            });

            // n nearest neighbors for this point
            const nearestNeighbors = distances
                .sort((a, b) => a.distance - b.distance)
                .slice(0, n)
                .map(d => d.index);

            // add to set of all neighbors
            nearestNeighbors.forEach(index => neighborSet.add(index));
        });

        // add originally selected points to the set
        selectedIndices.forEach(index => neighborSet.add(index));

        // set to array
        const allNeighbors = Array.from(neighborSet);

        if (scatterplot) {
            scatterplot.select(allNeighbors);
        }
    };

    const selectNeighborsMultiple = (n = 500) => {
        if (!selectedSampleInfo || selectedSampleInfo.length === 1 || !points || points.length === 0) {
            console.log("multiple samples must be selected");
            return;
        }


        // get indices of selected points
        const selectedIndices = selectedSampleInfo.map(id => ids.indexOf(id));
        // console.log(selectedIndices);

        const selectedSet = new Set(selectedIndices);

        // spatial index for faster nearest neighbor search
        const pointMap = new Map();
        points.forEach((point, index) => {
            if (!selectedSet.has(index)) {
                pointMap.set(index, point);
            }
        });

        // store all neighbor candidates with their minimum distance to any selected point
        const neighborCandidates = new Map();

        // for each unselected point, find its minimum distance to any selected point
        pointMap.forEach((point, index) => {
            let minDistance = Infinity;

            // find minimum distance to any selected point
            for (const selectedIndex of selectedIndices) {
                const selectedPoint = points[selectedIndex];
                const dx = point[0] - selectedPoint[0];
                const dy = point[1] - selectedPoint[1];
                const distance = dx * dx + dy * dy;
                minDistance = Math.min(minDistance, distance);
            }

            neighborCandidates.set(index, minDistance);
        });

        // convert to array and sort by distance
        const sortedNeighbors = Array.from(neighborCandidates.entries())
            .sort((a, b) => a[1] - b[1])
            .slice(0, n)
            .map(([index]) => index);

        // add originally selected points
        const allNeighbors = [...sortedNeighbors, ...selectedIndices];

        if (scatterplot) {
            scatterplot.select(allNeighbors);
        }
    };

    const navigate = useNavigate();


    const samplePage = () => {
        navigate('/sample', { state: { selectedSample: selectedSampleInfo } });
    };

    const handleTabChange = (tab) => {
        setSelectedTab(tab);
        // console.log("set tab to: ", tab);
    };




    if (!selectedSampleInfo || selectedSampleInfo.length === 0) {
        // return <p>No sample selected</p>;
        return <></>;
    }

    if (selectedSampleInfo.length === 1 && singleSampleData && singleSampleData.meta_data) {
        if (singleSampleData && singleSampleData.meta_data) {
            const biome_sample = singleSampleData.meta_data.source_biome.replace(/_/g, ' ').replace(/:/g, ' ').replace(/root/g, ' ');
            const arr = [
                `ID: ${selectedSampleInfo}`,
                <br />, `Biome: ${biome_sample}`,

                // <br />, `Sequencer: ${singleSampleData.meta_data.instrument_model}`,
                // <br />, `Location: ${sampleData.meta_data.geographic_location}`,
                // <br />, `Location: ${singleSampleData.meta_data?.geographic_location || 'N/A'}`,
                // <br />, `Confidence: ${singleSampleData?.confidence_score || 'N/A'}`,
                // <br />, `Host sex: ${sampleData.meta_data.host_sex}`,];
                // < br />, `Host sex: ${singleSampleData.meta_data.host_sex || 'N/A'}`,];
            ];

            return (
                <div style={divStyleRight}>
                    <div style={headerStyle}>
                        <h2 style={{ margin: 0 }}>1 Sample Information</h2>
                    </div>
                    <p> {arr} </p>
                    <HorizontalColoredLine margin={10} />
                    <NeighborSelector
                        value={neighborCount}
                        onChange={setNeighborCount}
                        onSelect={selectNeighbors}
                        stepSize={10}
                        maxValue={5000}
                    />
                    <HorizontalColoredLine margin={10} />

                    {/* simple button to increase or decrease the sunburst plot depth */}

                    <div style={{ marginBottom: '10px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <Button
                            onClick={toggleDomain}
                            disabled={!availableDomains.includes('archaea')}
                            style={{
                                opacity: availableDomains.includes('archaea') ? 1 : 0.5,
                                cursor: availableDomains.includes('archaea') ? 'pointer' : 'not-allowed'
                            }}
                        >
                            Switch to {domain === 'bacteria' ? 'Archaea' : 'Bacteria'}
                        </Button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Button
                                onClick={() => setSunburstDepth(prev => Math.max(2, prev - 1))}
                                style={{
                                    minWidth: '50px',
                                    width: '50px',
                                    minHeight: '50px',
                                    fontSize: '20px',
                                    padding: '0px',
                                    margin: '0px'
                                }}
                            >
                                -
                            </Button>
                            <span style={{
                                color: theme.colors.text,
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                            }}
                            >{sunburstDepth}</span>
                            <Button
                                onClick={() => setSunburstDepth(prev => Math.min(6, prev + 1))}
                                style={{
                                    minWidth: '50px',
                                    width: '50px',
                                    minHeight: '50px',
                                    fontSize: '20px',
                                    padding: '0px',
                                    margin: '0px'
                                }}
                            >
                                +
                            </Button>
                        </div>
                    </div>


                    {/* <SunburstPlot taxaData={taxonomyTreeData} highlightTaxon={"Candidatus Saccharibacteria"} /> */}
                    {/* <SunburstPlot taxaData={taxonomyTreeData} highlightTaxon={"Rhodospirillales"} /> */}
                    <SunburstPlot
                        taxaData={taxonomyTreeData}
                        highlightTaxon={highlightTaxon}
                        setHighlightTaxon={setHighlightTaxon}
                        depth={sunburstDepth} // this value can be changed to increase or decrease the depth of the sunburst plot
                        domain={domain}
                    />
                </div>
            );
        } else {
            return (
                <div style={divStyleRight}>
                    <div style={headerStyle}>
                        <h2 style={{ margin: 0 }}>No Data Available</h2>
                    </div>
                </div>
            );
        }
    } else
        if (selectedSampleInfo.length > 1) {
            return (
                <div style={divStyleRight}>
                    <div style={headerStyle}>
                        <h2 style={{ margin: 0 }}>{selectedSampleInfo.length} Samples selected</h2>
                    </div>

                    {/* <NeighborSelector
                        value={neighborCountMultiple}
                        onChange={setNeighborCountMultiple}
                        onSelect={(n) => selectNeighborsMultiple(n)}
                        // onSelect={(n) => selectNeighborsMultipleForEach(n)}
                        stepSize={100}
                        maxValue={1000}
                    /> */}
                    <NeighborSelectorMultiple
                        value={neighborCountMultiple}
                        onChange={setNeighborCountMultiple}
                        onSelectMultiple={(n) => selectNeighborsMultiple(n)}
                        onSelectForEach={(n) => selectNeighborsMultipleForEach(n)}
                        stepSize={100}
                        maxValue={1000}
                        selectionMethod={neighborSelectionMethod}
                        onSelectionMethodChange={setNeighborSelectionMethod}
                    />

                    {/* <NeighborSelector
                        value={neighborCountMultiple}
                        onChange={setNeighborCountMultiple}
                        // onSelect={(n) => selectNeighborsMultiple(n)}
                    onSelect={(n) => selectNeighborsMultipleForEach(n)}
                    // stepSize={100}
                    // maxValue={1000}
                    /> */}


                    {/* always show tabs - don't wait for all data */}
                    <HorizontalTabs
                        // items={['Biome', 'Taxonomic', 'Studies', "Enrichment", "UMAP", "Barchart"]}
                        items={['Biome', 'Studies', "Enrichment", "UMAP", "Barchart"]}
                        onSelect={(item) => handleTabChange(item)}
                        initialSelectedItem={selectedTab}
                    />

                    {/* render tab content based on available data */}
                    {
                        selectedTab === 'Biome' && (
                            // !multipleSamplesMetaData ? (
                            loadingMultipleSamplesMetaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading biome data...</p>
                                </div>
                            ) : (
                                <SubSampleBiomeColorOther
                                    multipleSamplesMetaData={multipleSamplesMetaData}
                                    possibleColorDict={possibleColorDict}
                                    scatterplot={scatterplot}
                                    colors={colors}
                                    colorDict={colorDict}
                                    ids={ids}
                                    currentPointsSelection={currentPointsSelection}
                                    totalBiomeCounts={totalBiomeCounts}
                                />
                            )
                        )
                    }

                    {
                        selectedTab === 'Taxonomic' && (
                            loadingMultipleSamplesTaxaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading taxonomic data...</p>
                                </div>
                            ) : !taxonomyTreeDataMultiple ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>No taxonomic data available</p>
                                </div>
                            ) : (
                                <SubSampleTaxonomy
                                    taxonomyTreeDataMultiple={taxonomyTreeDataMultiple}
                                    highlightTaxon={highlightTaxon}
                                    setHighlightTaxon={setHighlightTaxon}
                                />
                                // {/* </div> */}
                            )
                        )
                    }

                    {
                        selectedTab === 'Studies' && (
                            // !multipleSamplesMetaData ? (
                            loadingMultipleSamplesMetaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading study data...</p>
                                </div>
                            ) : (
                                <SubSampleStudies multipleSamplesMetaData={multipleSamplesMetaData} />
                            )
                        )
                    }

                    {
                        selectedTab === 'Enrichment' && (
                            selectedSampleInfo.length > 5000 ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Selection exceeds maximum of 5000 samples.</p>
                                </div>
                            ) : loadingMultipleIndividualSamplesTaxaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading enrichment data...</p>
                                </div>
                            ) : (
                                <SubSampleEnrichmentScatter
                                    multipleSamplesMetaData={multipleSamplesMetaData}
                                    multipleSamplesTaxaDataIndividual={multipleSamplesTaxaDataIndividual}
                                    isLoadingIndividualTaxa={loadingMultipleIndividualSamplesTaxaData}
                                />
                            )
                        )
                    }

                    {
                        selectedTab === 'UMAP' && (
                            selectedSampleInfo.length > 1000 ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Selection exceeds maximum of 1000 samples.
                                        please contact <a href="mailto:veo.lab@uni-jena.de">veo.lab@uni-jena.de</a>
                                    </p>
                                </div>
                            ) : loadingMultipleIndividualSamplesTaxaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading UMAP data...</p>
                                </div>
                            ) : (
                                <SubSampleUMAP
                                    multipleSamplesMetaData={multipleSamplesMetaData}
                                    multipleSamplesTaxaDataIndividual={multipleSamplesTaxaDataIndividual}
                                    isLoadingIndividualTaxa={loadingMultipleIndividualSamplesTaxaData}
                                    possibleColorDict={possibleColorDict}
                                />
                            )
                        )
                    }
                    {
                        selectedTab === 'Barchart' && (
                            selectedSampleInfo.length > 1000 ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Selection exceeds maximum of 1000 samples.
                                        please contact <a href="mailto:veo.lab@uni-jena.de">veo.lab@uni-jena.de</a>
                                    </p>
                                </div>
                            ) : loadingMultipleIndividualSamplesTaxaData ? (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <p>Loading UMAP data...</p>
                                </div>
                            ) : (
                                <SubSampleTaxaDistribution
                                    multipleSamplesMetaData={multipleSamplesMetaData}
                                    multipleSamplesTaxaDataIndividual={multipleSamplesTaxaDataIndividual}
                                    isLoadingIndividualTaxa={loadingMultipleIndividualSamplesTaxaData}
                                    points={points}
                                    ids={ids}
                                />
                            )
                        )
                    }
                </div >
            );
        } else
            return (
                <div style={divStyleRight}>
                    <div style={headerStyle}>
                        <h2 style={{ margin: 0 }}>Loading</h2>
                    </div>
                </div>

            );
};


export default TopRightMenu;